"use client";
import {JSX, memo} from "react";

import {useSnapshot} from "valtio";

import {CheckAvailability, CheckAvailabilityInstance, Payload} from "@/features/rental/check-availability/check-availability.model";
import {BlockActions} from "@/features/rental/check-availability/parts/block-actions";
import {MobileViewport} from "@/features/rental/check-availability/parts/mobile-viewport";
import {PriceHeader} from "@/features/rental/check-availability/parts/price-header";
import {InquiryFormModal} from "@/features/rental/inquiry-form";

import {useModel, useRouterBridge, useServerRouter} from "@/shared/hooks";
import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {useTranslation} from "@/shared/i18n";
import {Rating} from "@/shared/ui/data-display/rating";
import {Typography} from "@/shared/ui/data-display/typography";

import "@/features/rental/check-availability/styles/check-availability.scss";

export interface CheckAvailabilityProps {
    payload: Payload;
    rating?: number;
    reviewsCount?: number;
    pricePerNight?: number;
    currencySymbol?: string;
    className?: string;
}

export const CheckAvailabilityView = memo(function CheckAvailabilityView({
    payload,
    rating,
    reviewsCount,
    pricePerNight,
    currencySymbol = "$",
    className = "",
}: CheckAvailabilityProps): JSX.Element {
    const {t} = useTranslation("reviews");
    const {t: tBooking} = useTranslation("booking");

    const [model] = useModel<CheckAvailabilityInstance, [Payload?]>(CheckAvailability, {...payload, minNights: 2});
    const snapshot = useSnapshot(model);

    useRouterBridge(model, {skipInitialSync: true});
    useServerRouter(model);

    const isMobileViewport = useMatchMedia({maxWidth: 767});
    const handleScrollToReviews = (): void => {
        const reviewsSection = document.getElementById("reviews-section");
        if (reviewsSection) {
            reviewsSection.scrollIntoView({behavior: "smooth"});
        }
    };
    return (
        <>
            <section className={`check-availability ${isMobileViewport ? "check-availability--mobile-hidden" : ""} ${className}`}>
                {snapshot?.bookingType === "inquiry" && (
                    <div className="check-availability__inquiry-banner">
                        <div className="check-availability__inquiry-banner-icon">i</div>
                        <div className="check-availability__inquiry-banner-content">
                            <span className="check-availability__inquiry-banner-title">{tBooking("inquiryOnly")}</span>
                            <span className="check-availability__inquiry-banner-text">{tBooking("inquiryOnlyMessage")}</span>
                        </div>
                    </div>
                )}

                <div className="check-availability__header">
                    <PriceHeader
                        initialPrice={pricePerNight}
                        totalPrice={snapshot.totalPrice}
                        currencySymbol={currencySymbol}
                        loading={snapshot.isQuoteLoading}
                        hasDates={snapshot.hasDates}
                    />

                    {!!rating && !!reviewsCount && (
                        <div className="check-availability__rating">
                            <Rating value={rating} size="large" decimals={2} />
                            {rating > 0 && (
                                <button className="check-availability__reviews-link" onClick={handleScrollToReviews}>
                                    <Typography as="span" size={14} weight={400} color="primary" className="check-availability__reviews-count">
                                        {t("count", {count: reviewsCount})}
                                    </Typography>
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {!isMobileViewport && <BlockActions model={model} className="check-availability__form" />}
            </section>

            {isMobileViewport && payload && <MobileViewport model={model} pricePerNight={pricePerNight} currencySymbol={currencySymbol} />}

            <InquiryFormModal model={model.inquiryForm} isOpen={snapshot.isInquiryOpen} onClose={() => model.closeInquiry()} />
        </>
    );
});
